/**
 * ABOUTME: Selection view for SmartQuote with trade category scrolling and tradesperson selection
 * ABOUTME: Handles trade selection, category filtering, and message composition
 */
import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tradesperson } from '@/lib/types';
import { toast } from 'sonner';
import { debug } from '@/lib/utils';
import { getTradeIconForTrade, filterTradesByCategoryId, getTradeType } from '@/lib/tradeUtils';
import { TRADE_CATEGORIES } from '@/lib/tradeCategories';

interface SmartQuoteSelectionViewProps {
  fullMessage: string;
  setFullMessage: (message: string) => void;
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  tradespeople: Tradesperson[];
  filteredTradespeople: Tradesperson[];
  selectedTradespeople: string[];
  handleTradeSelection: (tradeId: string) => void;
  visibleCategories: string[];
  moveToContinue: () => void;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  handleScrollLeft: () => void;
  handleScrollRight: () => void;
  checkScrollPosition: () => void;
}

export function SmartQuoteSelectionView({
  fullMessage,
  setFullMessage,
  selectedCategory,
  setSelectedCategory,
  tradespeople,
  filteredTradespeople,
  selectedTradespeople,
  handleTradeSelection,
  visibleCategories,
  moveToContinue,
  canScrollLeft,
  canScrollRight,
  handleScrollLeft,
  handleScrollRight,
  checkScrollPosition
}: SmartQuoteSelectionViewProps) {
  // Reference for the scrollable categories container
  const categoriesScrollRef = useRef<HTMLDivElement>(null);

  // Handle category selection
  const handleCategorySelect = useCallback((category: string) => {
    debug.log(`Selecting category: ${category}`);

    // Toggle selected category
    if (selectedCategory === category) {
      debug.log(`Toggling off category: ${category}`);
      setSelectedCategory(null);
      return;
    }

    setSelectedCategory(category);
  }, [selectedCategory, setSelectedCategory]);

  return (
    <div>
      {/* Horizontal category bubbles */}
      <div className="mb-2">
        <div className="relative">
          {/* Category list with navigation arrows */}
          <div className="relative flex items-center">
            {/* Left scroll arrow - only visible when we can scroll left */}
            {canScrollLeft && (
              <button
                className="absolute left-0 z-20 bg-white/80 backdrop-blur-sm rounded-full shadow-md w-6 h-6 flex items-center justify-center text-purple-700 hover:bg-purple-50"
                onClick={handleScrollLeft}
                aria-label="Scroll left"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}

            {/* Scrollable container - hide scrollbar */}
            <div
              ref={categoriesScrollRef}
              className="overflow-x-auto mx-6 no-scrollbar"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onScroll={checkScrollPosition}
            >
              <div className="flex items-center py-1 px-1 min-w-full pr-6">
                {/* Map visible categories to bubbles */}
                {visibleCategories.map((category) => {
                  const isActive = selectedCategory === category;
                  const tradeCategory = TRADE_CATEGORIES.find(t =>
                    t.id.toLowerCase() === category.toLowerCase() ||
                    t.displayName.toLowerCase() === category.toLowerCase()
                  );

                  const TradeIcon = tradeCategory?.icon ||
                    getTradeIconForTrade({ trade_type: category } as Tradesperson);

                  const displayName = tradeCategory?.displayName || category;

                  return (
                    <button
                      key={category}
                      onClick={() => handleCategorySelect(category)}
                      title={isActive ? `${displayName} (tap again to clear)` : displayName}
                      aria-label={isActive ? `Clear ${displayName} filter` : `Filter by ${displayName}`}
                      className={`flex items-center justify-center rounded-full w-10 h-10 flex-shrink-0 mr-2 transition-colors ${
                        isActive
                          ? 'bg-purple-700 shadow-md'
                          : 'bg-purple-100 hover:bg-purple-200'
                      }`}
                    >
                      <TradeIcon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-purple-700'}`} />
                    </button>
                  );
                })}

                {/* Extra padding at the end to ensure last item doesn't get cut by gradient */}
                <div className="w-1 flex-shrink-0"></div>
              </div>
            </div>

            {/* Right scroll arrow - only visible when we can scroll right */}
            {canScrollRight && (
              <button
                className="absolute right-0 z-20 bg-white/80 backdrop-blur-sm rounded-full shadow-md w-6 h-6 flex items-center justify-center text-purple-700 hover:bg-purple-50"
                onClick={handleScrollRight}
                aria-label="Scroll right"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Selected tradesperson section */}
      <div className="space-y-1 mb-2">
        {selectedTradespeople.length > 0 ? (
          // Show selected tradespeople
          selectedTradespeople.map(tradeId => {
            const trade = tradespeople.find(t => t.id === tradeId);
            if (!trade) return null;

            const TradeIcon = getTradeIconForTrade(trade);

            return (
              <div
                key={trade.id}
                onClick={() => handleTradeSelection(trade.id)}
                className="flex items-center p-1.5 rounded-lg bg-purple-50 border border-purple-200 cursor-pointer"
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center mr-2 bg-purple-100">
                  <TradeIcon className="h-3.5 w-3.5 text-purple-700" />
                </div>

                <div className="flex-1">
                  <div className="font-medium text-sm">
                    {trade.org || trade.contact}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">
                    {trade.trade_type || trade.category || 'General trade'}
                  </div>
                </div>

                <div className="pl-1">
                  <div className="h-5 w-5 rounded-full bg-purple-600 flex items-center justify-center">
                    <CheckCircle className="h-3 w-3 text-white" />
                  </div>
                </div>
              </div>
            );
          })
        ) : null}

        {/* Only show tradespeople if a category is selected */}
        {selectedCategory ? (
          filteredTradespeople.length > 0 ? (
            // Show filtered tradespeople based on category (up to 5)
            filteredTradespeople.slice(0, 5).map(trade => {
              // Skip already selected tradespeople
              if (selectedTradespeople.includes(trade.id)) return null;

              const TradeIcon = getTradeIconForTrade(trade);

              return (
                <div
                  key={trade.id}
                  onClick={() => handleTradeSelection(trade.id)}
                  className="flex items-center p-1.5 rounded-lg hover:bg-gray-50 border border-transparent cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center mr-2 bg-gray-100">
                    <TradeIcon className="h-3.5 w-3.5 text-gray-500" />
                  </div>

                  <div className="flex-1">
                    <div className="font-medium text-sm">
                      {trade.org || trade.contact}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {trade.trade_type || trade.category || 'General trade'}
                    </div>
                  </div>

                  <div className="pl-1">
                    <div className="h-5 w-5 rounded-full border border-gray-300"></div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-2 text-xs text-gray-500">
              No tradespeople found for {selectedCategory}
            </div>
          )
        ) : (
          <div className="text-center py-6 px-2">
            <div className="text-purple-700 text-sm font-medium mb-1">Please select a category</div>
            <p className="text-xs text-gray-500">
              Choose a trade category above to see available tradespeople
            </p>
          </div>
        )}
      </div>

      {/* Message input - only shown after category is selected */}
      {selectedCategory && (
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-500">Your message:</span>
            {selectedTradespeople.length > 0 && (
              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full text-[10px]">
                {selectedTradespeople.length} selected
              </span>
            )}
          </div>
          <textarea
            value={fullMessage}
            onChange={(e) => setFullMessage(e.target.value)}
            className="w-full border border-gray-200 rounded-lg p-3 text-sm text-gray-800 resize-none h-[180px] focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-purple-300"
            placeholder="Describe what you need..."
          />
        </div>
      )}

      {/* Continue button - only shown after category is selected */}
      {selectedCategory && (
        <div className="flex justify-end">
          <Button
            onClick={moveToContinue}
            className={`rounded-full shadow-sm px-4 py-1 flex items-center text-sm ${
              selectedTradespeople.length === 0
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
            disabled={selectedTradespeople.length === 0}
          >
            <span>Continue</span>
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}